import { theme, baseStyle } from "../../theme";

/**
 * AuthScreen handles three auth states:
 * 1. Auth0 loading spinner
 * 2. Auth0 login (Google + email)
 * 3. Dev mode user selection
 */
export default function AuthScreen({ isDev, isLoading, isAuthenticated, loginWithRedirect, devUser, setDevUser, notify }) {

  // ---- AUTH0 LOADING ----
  if (!isDev && isLoading) {
    return (
      <div style={baseStyle}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: 32 }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🚛</div>
          <h1 style={{ fontSize: 42, fontWeight: 800, letterSpacing: -2, margin: 0, background: `linear-gradient(135deg, ${theme.accent}, ${theme.green})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Greiði</h1>
          <p style={{ color: theme.textMuted, fontSize: 15, marginTop: 24 }}>Hleð inn...</p>
        </div>
      </div>
    );
  }

  // ---- LOGIN (Auth0: SMS, Google, or email) ----
  if (!isDev && !isAuthenticated) {
    return (
      <div style={baseStyle}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: 32 }}>
          <div style={{ fontSize: 64, marginBottom: 8 }}>🚛</div>
          <h1 style={{ fontSize: 42, fontWeight: 800, letterSpacing: -2, margin: 0, background: `linear-gradient(135deg, ${theme.accent}, ${theme.green})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Greiði</h1>
          <p style={{ color: theme.textMuted, fontSize: 15, marginTop: 4, marginBottom: 40, textAlign: "center" }}>Taktu mynd. Fáðu hjálp. Einfalt.</p>

          <div style={{ width: "100%", maxWidth: 320 }}>
            {/* SMS / Símanúmer — aðal innskráning */}
            <button
              onClick={() => loginWithRedirect({
                authorizationParams: {
                  connection: 'sms',
                },
              })}
              style={{
                width: "100%",
                padding: "16px",
                borderRadius: 12,
                border: "none",
                background: `linear-gradient(135deg, ${theme.green}, #10b981)`,
                color: "#0a0f1a",
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
                marginBottom: 12,
                letterSpacing: 0.5,
              }}
            >
              <span style={{ fontSize: 20 }}>📱</span>
              Innskrá með símanúmeri
            </button>

            <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "16px 0" }}>
              <div style={{ flex: 1, height: 1, background: theme.border }} />
              <span style={{ color: theme.textDim, fontSize: 13 }}>eða tengdu Google</span>
              <div style={{ flex: 1, height: 1, background: theme.border }} />
            </div>

            {/* Google — tengja við SMS aðgang */}
            <button
              onClick={() => loginWithRedirect({
                authorizationParams: {
                  connection: 'google-oauth2',
                  prompt: 'select_account',
                },
              })}
              style={{
                width: "100%",
                padding: "16px",
                borderRadius: 12,
                border: `1px solid ${theme.border}`,
                background: "white",
                color: "#1f1f1f",
                fontSize: 16,
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
                <path d="M9.003 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332C2.438 15.983 5.482 18 9.003 18z" fill="#34A853"/>
                <path d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.96H.957C.347 6.175 0 7.55 0 9.002c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.426 0 9.002 0 5.48 0 2.438 2.017.96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71z" fill="#EA4335"/>
              </svg>
              Innskrá með Google
            </button>

            {/* Netfang — aukaleið */}
            <button
              onClick={() => loginWithRedirect()}
              style={{
                width: "100%",
                padding: "14px",
                borderRadius: 12,
                border: `1px solid ${theme.border}`,
                background: "transparent",
                color: theme.textMuted,
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Innskrá með netfangi →
            </button>
          </div>

          <p style={{ color: theme.textDim, fontSize: 12, marginTop: 32, textAlign: "center", lineHeight: 1.6 }}>
            Með því að halda áfram samþykkir þú<br />
            <span style={{ color: theme.accent, cursor: "pointer" }}>skilmála</span> og <span style={{ color: theme.accent, cursor: "pointer" }}>persónuverndarstefnu</span>
          </p>
        </div>
      </div>
    );
  }

  // ---- DEV MODE LOGIN ----
  if (isDev && !devUser) {
    return (
      <div style={baseStyle}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: 32 }}>
          <div style={{ fontSize: 64, marginBottom: 8 }}>🚛</div>
          <h1 style={{ fontSize: 42, fontWeight: 800, letterSpacing: -2, margin: 0, background: `linear-gradient(135deg, ${theme.accent}, ${theme.green})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Greiði</h1>
          <p style={{ color: theme.textMuted, fontSize: 15, marginTop: 4, marginBottom: 24, textAlign: "center" }}>Dev Mode - Velja notanda</p>

          <div style={{ width: "100%", maxWidth: 360, background: theme.card, border: `2px solid ${theme.accent}`, borderRadius: 16, padding: 16, marginBottom: 20 }}>
            <div style={{ fontSize: 13, color: theme.accent, fontWeight: 700, marginBottom: 12, textAlign: "center" }}>⚙️ ÞRÓUNARHAMUR</div>
            <p style={{ fontSize: 12, color: theme.textMuted, margin: 0, textAlign: "center", lineHeight: 1.5 }}>
              Auth0 er ekki uppsett. Veldu notanda til að prófa appið.
            </p>
          </div>

          <div style={{ width: "100%", maxWidth: 360 }}>
            {[
              { id: "dev-1", name: "Hinrik Próf", avatar: "👨‍🔧", phone: "899-0001", rating: 4.9, jobs: 12, featured: true },
              { id: "dev-2", name: "Sigga Dev", avatar: "👩‍💼", phone: "899-0002", rating: 4.7, jobs: 5, featured: false },
              { id: "dev-3", name: "Jón Test", avatar: "🧑‍🏭", phone: "899-0003", rating: 5.0, jobs: 0, featured: false },
            ].map(u => (
              <button
                key={u.id}
                onClick={() => {
                  setDevUser(u);
                  notify(`✅ Skráður inn sem ${u.name}`);
                }}
                style={{
                  width: "100%",
                  padding: "16px",
                  marginBottom: 12,
                  borderRadius: 12,
                  border: u.featured ? `2px solid ${theme.accent}` : `1px solid ${theme.border}`,
                  background: u.featured ? theme.accentGlow : theme.card,
                  color: theme.text,
                  fontSize: 15,
                  fontWeight: u.featured ? 700 : 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  textAlign: "left",
                  position: "relative",
                }}
              >
                <span style={{ fontSize: 32 }}>{u.avatar}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700 }}>{u.name}</div>
                  <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>
                    +354 {(u.phone || '').replace(/\+354\s*/g, '').trim()} · ⭐ {u.rating} · {u.jobs} verk
                  </div>
                </div>
                {u.featured && (
                  <span style={{ position: "absolute", top: 8, right: 8, fontSize: 10, background: theme.accent, color: "#0a0f1a", padding: "3px 8px", borderRadius: 6, fontWeight: 700 }}>
                    SÚPER
                  </span>
                )}
              </button>
            ))}
          </div>

          <p style={{ color: theme.textDim, fontSize: 11, marginTop: 24, textAlign: "center", lineHeight: 1.6 }}>
            Til að nota Auth0 innskráningu:<br />
            Settu upp <code style={{ background: theme.card, padding: "2px 6px", borderRadius: 4 }}>VITE_AUTH0_DOMAIN</code> í .env
          </p>
        </div>
      </div>
    );
  }

  // If none of the auth conditions apply, return null
  // (means user is authenticated and parent should render main app)
  return null;
}
